import { useState, useEffect, useCallback } from "react";

export type NetworkQuality = "good" | "slow" | "offline" | "error";

interface NetworkQualityState {
  quality: NetworkQuality;
  latency: number | null;
  error: string | null;
}

const HEALTH_CHECK_URL = "/api/health/ping"; // Reutiliza tu endpoint de health
const TIMEOUT_MS = 5000; // Timeout de 5s para considerar conexión lenta
const SLOW_THRESHOLD_MS = 3000; // Umbral para considerar conexión lenta

export const useNetworkQuality = () => {
  const [networkState, setNetworkState] = useState<NetworkQualityState>({
    quality: navigator.onLine ? "good" : "offline",
    latency: null,
    error: null,
  });

  const checkNetworkQuality = useCallback(async (signal?: AbortSignal) => {
    if (!navigator.onLine) {
      setNetworkState({ quality: "offline", latency: null, error: null });
      return;
    }

    try {
      const controller = signal ? null : new AbortController();
      const timeoutId = setTimeout(() => controller?.abort(), TIMEOUT_MS);

      const start = Date.now();
      const response = await fetch(HEALTH_CHECK_URL, {
        method: "HEAD",
        cache: "no-cache",
        signal: signal || controller?.signal,
      });
      const latency = Date.now() - start;

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error("Respuesta no válida del servidor");
      }

      setNetworkState({
        quality: latency > SLOW_THRESHOLD_MS ? "slow" : "good",
        latency,
        error: null,
      });
    } catch (err) {
      setNetworkState({
        quality: err instanceof Error && err.name === "AbortError" ? "slow" : "error",
        latency: null,
        error: err instanceof Error ? err.message : "Error desconocido",
      });
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setNetworkState((prev) => ({ ...prev, quality: "good", error: null }));
      checkNetworkQuality();
    };
    const handleOffline = () => {
      setNetworkState({ quality: "offline", latency: null, error: null });
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Verificar al montar
    checkNetworkQuality();

    // Opcional: Verificación periódica (cada 30s) si no offline
    const interval = setInterval(() => {
      if (navigator.onLine) {
        checkNetworkQuality();
      }
    }, 30000);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(interval);
    };
  }, [checkNetworkQuality]);

  return { networkState, checkNetworkQuality };
};